import { Breadcrumbs } from '@digital-retail/falabella-ui-cross-components';
import { isStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import apiConfig from '../config/api/apiConfig';
import { constants } from '../config/constants';
import { storeMap } from '../config/storeMap';
import ProductLandingContainer from '../containers/ProductLanding/ProductLandingContainer';
import { getHostUrl } from '../containers/SearchResults/utils';
import { withApplicationContext } from '../utils/ApplicationContext';
import { CartContextProvider } from '../utils/CartContext';
import { CookieStorage } from '../utils/CookieStorage';
import { navigate, redirect } from '../utils/RouteHandler';
import { PLPSeo } from '../utils/SEO/PLPSeo';
import { getHeadersForSponsors } from '../utils/SponsoredHelper';
import { WebStorage } from '../utils/WebStorage';
import { WithUrl } from '../utils/WithUrl';
import { addProductListEvent } from '../utils/dataLayerHelper';
import { isWindow } from '../utils/deviceChecker';
import { useOnScroll } from '../utils/hooks/UseOnScroll';
import { httpService } from '../utils/httpService';
import { replaceImageDimensions } from '../utils/imageUrlHelper';
import { isKiosk } from '../utils/kisokHelper';
import { handleScroll } from '../utils/paginationUtils';
import { cleanText } from '../utils/textHelper';
import { getPaginatedUrl as getPaginatedUrlUtil, getRequestUrl } from '../utils/urlHelper';
import Error from './_error';

const updateLocation = () => {
  WebStorage.setItem({
    name: constants.SESSION_STORAGE_SCROLL,
    value: `${window.scrollX},${window.scrollY}`,
  });
};

const Category = (props) => {
  const { statusCode } = props;
  // eslint-disable-next-line react/prop-types
  const { appCtx, ...initialData } = props; // InitialData should be empty
  const { regionCode, store } = appCtx;

  const data = useRef();
  const isInitialDataEmpty = Object.entries(initialData).length === 0;
  if (!isInitialDataEmpty) {
    data.current = initialData;
  }

  const {
    searchTerm,
    pagination,
    facets,
    promotedFilter,
    baseFacetsURL,
    appliedFacets,
    autoCorrections,
    availabilityFacets,
    results,
    sortOptions,
    currentLocationId,
    site,
    categoryParentDetails,
    breadCrumbData,
    metadata,
    categoryBannerHtml,
    leftPromotionContent,
    canonicalUrl,
    currentUrl,
    query,
    layout,
  } = data.current || {};

  useEffect(() => {
    appCtx.attachResizeListener();
  }, [data.current]);

  useEffect(() => {
    handleScroll(appCtx);
    if (results) {
      addProductListEvent(results, 'category', store, regionCode);
    }
  }, [results]);

  useOnScroll({
    handler: updateLocation,
  });

  const getPaginatedUrl = (paginationIndex) => {
    return getPaginatedUrlUtil(currentUrl, paginationIndex, store, regionCode);
  };

  if (statusCode === 500) {
    return <Error />;
  }

  return !isInitialDataEmpty ? (
    <>
      <PLPSeo
        SiteProps={{
          query,
          regionCode,
          metadata,
          canonicalUrl,
          breadcrumb: breadCrumbData,
          store,
          appliedFacets,
          facets,
        }}
      />
      <Breadcrumbs items={breadCrumbData} host={appCtx.host} tenant={appCtx.regionCode} path={appCtx.url} />
      <CartContextProvider>
        <ProductLandingContainer
          pagination={{
            data: pagination,
            getPaginatedUrl,
          }}
          layout={layout}
          products={results}
          promotedFilter={promotedFilter}
          autoCorrections={autoCorrections}
          facets={facets}
          baseFacetsURL={baseFacetsURL}
          appliedFacets={appliedFacets}
          searchTerm={searchTerm}
          availabilityFacets={availabilityFacets}
          sortOptions={sortOptions}
          site={site}
          currentLocationId={currentLocationId}
          categoryParentDetails={categoryParentDetails}
          categoryBannerHtml={categoryBannerHtml}
          leftPromotionContent={leftPromotionContent}
          pageName="category"
          canonicalUrl={canonicalUrl}
          breadCrumbData={breadCrumbData}
        />
      </CartContextProvider>
    </>
  ) : null;
};

const shouldRedirect = (responseType) => {
  return responseType === constants.SEARCH_REDIRECT_STATUS;
};

const redirectCategory = (response, url, redirectCode = constants.HTTP_RES_CODE.TEMP_REDIRECT) => {
  if (isWindow()) {
    redirect(url, true);
    return;
  }

  if (response) {
    response.writeHead(redirectCode, { Location: url });
    response.end();
  }
};

const redirectNoSearchResult = (response, site, regionCode, termo, store) => {
  if (isWindow()) {
    navigate({
      url: '/noResult',
      params: {
        Ntt: termo,
      },
      site,
    });
    return;
  }

  if (response) {
    const url = `/${storeMap[store] || 'falabella'}-${regionCode}/noResult?Ntt=${termo}`;
    response.writeHead(constants.HTTP_RES_CODE.TEMP_REDIRECT, { Location: url });
    response.end();
  }
};

const getCanonicalUrl = (appCtx, region, categoryId, categoryName, query) => {
  const { page, store } = query;
  const cleanName = cleanText(categoryName);
  const storeValidated = store && storeMap[store.toLowerCase()] ? store : null;
  const url = WithUrl(
    isStandalone({ store }) ? 'SA_CATEGORY_PAGE' : 'CATEGORY_PAGE',
    region,
    {
      custom: {
        categoryId,
        categoryName: cleanName,
      },
    },
    storeValidated
  );

  let params = {};

  const facetsCounts = Object.keys(query).filter((name) => name.indexOf('f.') === 0).length;
  let friendlyFacetsCount = 0;

  constants.SEO_FRIENDLY_FACETS.forEach((facetId) => {
    if (query[facetId] && query[facetId].split('::').length === 1) {
      friendlyFacetsCount += 1;
      params[facetId] = encodeURI(query[facetId]).replace(/%20/g, '+');
    }
  });

  if (facetsCounts !== friendlyFacetsCount) {
    params = {};
  }

  if (page > 1) {
    params.page = page;
  }

  const keys = Object.keys(params);
  if (keys.length !== 0) {
    const paramString = keys.map((key) => `${key}=${params[key]}`).join('&');
    return `${url}?${paramString}`;
  }
  return url;
};

const isCanonicalUrl = (currentUrl, categoryName) => {
  const cleanName = cleanText(categoryName);
  const urlPath = currentUrl.match('(/{0,1}[^?#]*)')[0].split('/');
  const currentName = decodeURIComponent(urlPath[urlPath.length - 1]);
  return currentName === cleanName;
};

const shouldSEORedirect = (appCtx, currentUrl, categoryDisplayName) => {
  // Need to remove this once we get confirmation from SEO team
  const isRedirect301EnabledForPLP = appCtx?.siteConfig?.toggles?.isRedirect301EnabledForPLP ?? false;

  return isRedirect301EnabledForPLP && !isCanonicalUrl(currentUrl, categoryDisplayName);
};

const getUrlToRedirect = (currentUrl, canonicalUrl) => {
  const queryStrings = currentUrl.split('?')[1];
  const canonicalBaseUrl = canonicalUrl.split('?')[0];
  const canonicalQuerys = canonicalUrl.split('?')[1];
  const queryParams = {};
  if (canonicalQuerys) {
    canonicalQuerys.split('&').forEach((f) => {
      queryParams[f] = true;
    });
  }
  if (queryStrings) {
    queryStrings.split('&').forEach((f) => {
      if (f && !f.includes('subdomain') && !queryParams[f]) {
        queryParams[f] = true;
      }
    });
  }
  const paramsString = Object.keys(queryParams).join('&');
  const params = paramsString ? `?${paramsString}` : '';
  return `${encodeURI(canonicalBaseUrl)}${params}`;
};

Category.getInitialProps = async ({ config }, { query, req, res, asPath }, appCtx) => {
  const { site, id, name, ...rest } = query;
  const { regionCode: siteId, store, latLong } = appCtx;

  const searchTerm = encodeURIComponent(query.name);

  rest.categoryId = id;
  rest.categoryName = name;
  rest.pgid = appCtx.priceGroupId;
  rest.pid = appCtx.politicalId;
  rest.zones = appCtx.zones;
  if (latLong) {
    rest.latLong = encodeURIComponent(JSON.stringify(latLong));
  }
  const params = {};
  Object.keys(rest).forEach((key) => {
    const value = rest[key];
    if (value) {
      params[encodeURIComponent(key)] = encodeURIComponent(value);
    }
  });

  if (store) {
    params[encodeURIComponent('store')] = encodeURIComponent(store);
  }

  const isDebugAdsEnabled = isWindow() ? CookieStorage.getCookie('isDebugAdsEnabled') === 'true' : false;

  if (isDebugAdsEnabled) {
    params.debug = true;
  }
  if (isKiosk(appCtx)) {
    params.channel = 'KIOSK';
  }

  const cookies = CookieStorage.parseCookies(appCtx.ssrCookies);

  if (appCtx.isTottusSA) {
    if (cookies.USER_ID) {
      params.userId = cookies.USER_ID;
    }
    params.standardZones = appCtx.standardZones;
    params.expressZones = appCtx.expressZones;
  }

  const listingUrl = apiConfig.getListingsUrl({
    base: getHostUrl({ config, store, regionCode: siteId }),
    siteId,
    rest: params,
  });
  const currentLocationId = appCtx.politicalId;

  const headersForProductSearch = isWindow() ? {} : { cookie: appCtx.ssrCookies };
  const { deviceType, tenant } = appCtx;

  const headersForSponsors = getHeadersForSponsors(deviceType, tenant);
  const response = await httpService(appCtx).get(
    listingUrl,
    {
      includeTracing: true,
      reqId: req && req.id,
      headers: {
        'Content-Type': 'application/json',
        'x-device-type': appCtx.deviceType,
        ...appCtx.tracingHeaders,
        ...headersForProductSearch,
        ...headersForSponsors,
      },
    },
    'json'
  );

  const { data, error: listingApiError, statusCode } = response;

  if (listingApiError && statusCode !== 409) {
    res.statusCode = 500;
    return {
      statusCode: 500,
    };
  }

  if (shouldRedirect(data.responseType)) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      if (appCtx.isTottusSA) {
        res.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate, private, s-maxage=0');
        res.setHeader('Surrogate-Control', 'no-store');
        res.setHeader('Pragma', 'no-cache');
        res.setHeader('Vary', 'Cookie, X-Zone-Id');
        res.setHeader('X-Redirect-Dependent', 'zones');
        return redirectCategory(res, data.data.altUrl, 302);
      }
      res.setHeader('Cache-Control', 'no-cache');
    }
    return redirectCategory(res, data.data.altUrl, constants.HTTP_RES_CODE.PERM_REDIRECT);
  }

  const categoryParentDetails = data?.data?.categoryParentDetails ?? {};
  const breadCrumbData = data?.data?.breadcrumb ?? {};
  const metadata = data?.data?.metadata ?? {};
  const canonicalUrl = getCanonicalUrl(appCtx, siteId, metadata.id, metadata.displayName, rest);
  const currentUrl = isWindow() ? asPath : getRequestUrl(req.url, appCtx.tenant);

  const {
    data: {
      data: {
        layout,
        results,
        facets,
        appliedFacets,
        baseFacetsURL,
        availabilityFacets,
        promotedFilter,
        sortOptions,
        pagination,
        autoCorrections,
        relatedKeywordBanner,
        leftPromotionContent,
      },
    },
  } = response;

  if (results && results.length === 0) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    redirectNoSearchResult(res, site, appCtx.regionCode, '', store);
    return undefined;
  }

  if (
    // PDSLIST-964: redirect if category slug is not the same as displayName slug
    searchTerm !== cleanText(metadata.displayName) ||
    (!metadata.canonical && shouldSEORedirect(appCtx, currentUrl, metadata.displayName))
  ) {
    if (!isWindow() && res && !(res.finished || res.headersSent)) {
      res.setHeader('Cache-Control', 'no-cache');
    }
    const urlToRedirect = getUrlToRedirect(currentUrl, canonicalUrl);

    redirectCategory(res, urlToRedirect, constants.HTTP_RES_CODE.PERM_REDIRECT);
    return undefined;
  }

  if (!isWindow() && res && !(res.finished || res.headersSent)) {
    res.setHeader('Cache-Tag', 'catalyst-SLP-v1');
    const cacheKeyMaxAge = 365 * 24 * 60 * 60;
    res.setHeader(
      'Set-Cookie',
      `CACHE_KEY=${appCtx.buildId}; max-age=${cacheKeyMaxAge}; Path=/${appCtx.tenant}/search`
    );
  }

  let categoryBannerHtml = '';
  if (relatedKeywordBanner) {
    if (
      config.FETCH_BANNERS_IN_SERVER_SIDE === false &&
      (relatedKeywordBanner.includes('assets.contentstack') || relatedKeywordBanner.includes('assets.falabella'))
    ) {
      categoryBannerHtml = relatedKeywordBanner;
    } else {
      const { data: htmlData, error } = await httpService().get(relatedKeywordBanner, {
        includeTracing: false,
        asText: true,
      });

      if (!error) {
        categoryBannerHtml = replaceImageDimensions(htmlData, 500);
      }
    }
  }

  return {
    results,
    layout,
    facets,
    baseFacetsURL,
    appliedFacets,
    searchTerm,
    promotedFilter,
    pagination,
    sortOptions,
    autoCorrections,
    availabilityFacets,
    currentLocationId,
    site: encodeURIComponent(query.site),
    categoryParentDetails,
    breadCrumbData,
    metadata,
    categoryBannerHtml,
    leftPromotionContent,
    canonicalUrl,
    currentUrl,
    query,
  };
};

Category.propTypes = {
  layout: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  facets: PropTypes.arrayOf(PropTypes.object).isRequired,
  appliedFacets: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  site: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  sortOptions: PropTypes.array.isRequired,
  autoCorrections: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  baseFacetsURL: PropTypes.string.isRequired,
  currentLocationId: PropTypes.string.isRequired,
  categoryBannerHtml: PropTypes.string.isRequired,
  statusCode: PropTypes.number.isRequired,
  canonicalUrl: PropTypes.string.isRequired,
};

export { Category };

export default withApplicationContext(Category);
